/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteCredentialPathParameters,
  ExportCredentialToConnectorPathParameters,
  GetAllCredentialsParams,
  GetCredentialByIdPathParameters,
  PingCredentialByIdPathParameters,
  TLConnectorCredentialResponse,
  TLCredentialExportToConnectorRequest,
  TLCredentialPingRequest,
  TLCredentialResponse,
  TLCredentialsResponse,
  TLDataHarmonizationV2CredentialCreateRequest,
  TLDataHarmonizationV2CredentialUpdateRequest,
  TLHTTPValidationError,
  TLSuccessResponse,
  UpdateCredentialPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get All Credentials
 */
export const getAllCredentials = (
  params?: GetAllCredentialsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCredentialsResponse>(
    { url: `/v2/data-harmonization/credentials`, method: 'GET', params, signal },
    options,
  );
};

export const getGetAllCredentialsQueryKey = (params?: GetAllCredentialsParams) => {
  return ['v2', 'data-harmonization', 'credentials', ...(params ? [params] : [])] as const;
};

export const getGetAllCredentialsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllCredentialsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllCredentialsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllCredentials>>> = ({ signal }) =>
    getAllCredentials(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllCredentials>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllCredentialsQueryResult = NonNullable<Awaited<ReturnType<typeof getAllCredentials>>>;
export type GetAllCredentialsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAllCredentials<
  TData = Awaited<ReturnType<typeof getAllCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetAllCredentialsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCredentials>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllCredentials>>,
          TError,
          Awaited<ReturnType<typeof getAllCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllCredentials<
  TData = Awaited<ReturnType<typeof getAllCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllCredentialsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCredentials>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllCredentials>>,
          TError,
          Awaited<ReturnType<typeof getAllCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllCredentials<
  TData = Awaited<ReturnType<typeof getAllCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllCredentialsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Credentials
 */

export function useGetAllCredentials<
  TData = Awaited<ReturnType<typeof getAllCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllCredentialsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllCredentialsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Credentials
 */
export const prefetchGetAllCredentialsQuery = async <
  TData = Awaited<ReturnType<typeof getAllCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetAllCredentialsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllCredentialsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Credentials
 */
export const invalidateGetAllCredentials = async (
  queryClient: QueryClient,
  params?: GetAllCredentialsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllCredentialsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Credential
 */
export const createCredential = (
  tLDataHarmonizationV2CredentialCreateRequest: BodyType<TLDataHarmonizationV2CredentialCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCredentialResponse>(
    {
      url: `/v2/data-harmonization/credentials`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataHarmonizationV2CredentialCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateCredentialQueryKey = (
  tLDataHarmonizationV2CredentialCreateRequest?: BodyType<TLDataHarmonizationV2CredentialCreateRequest>,
) => {
  return ['POST', 'v2', 'data-harmonization', 'credentials', tLDataHarmonizationV2CredentialCreateRequest] as const;
};

export const getCreateCredentialQueryOptions = <
  TData = Awaited<ReturnType<typeof createCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataHarmonizationV2CredentialCreateRequest: BodyType<TLDataHarmonizationV2CredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateCredentialQueryKey(tLDataHarmonizationV2CredentialCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createCredential>>> = ({ signal }) =>
    createCredential(tLDataHarmonizationV2CredentialCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createCredential>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateCredentialQueryResult = NonNullable<Awaited<ReturnType<typeof createCredential>>>;
export type CreateCredentialQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateCredential<
  TData = Awaited<ReturnType<typeof createCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataHarmonizationV2CredentialCreateRequest: BodyType<TLDataHarmonizationV2CredentialCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCredential>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCredential>>,
          TError,
          Awaited<ReturnType<typeof createCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCredential<
  TData = Awaited<ReturnType<typeof createCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataHarmonizationV2CredentialCreateRequest: BodyType<TLDataHarmonizationV2CredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCredential>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCredential>>,
          TError,
          Awaited<ReturnType<typeof createCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCredential<
  TData = Awaited<ReturnType<typeof createCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataHarmonizationV2CredentialCreateRequest: BodyType<TLDataHarmonizationV2CredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Credential
 */

export function useCreateCredential<
  TData = Awaited<ReturnType<typeof createCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataHarmonizationV2CredentialCreateRequest: BodyType<TLDataHarmonizationV2CredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateCredentialQueryOptions(tLDataHarmonizationV2CredentialCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Credential
 */
export const prefetchCreateCredentialQuery = async <
  TData = Awaited<ReturnType<typeof createCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLDataHarmonizationV2CredentialCreateRequest: BodyType<TLDataHarmonizationV2CredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateCredentialQueryOptions(tLDataHarmonizationV2CredentialCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Credential
 */
export const invalidateCreateCredential = async (
  queryClient: QueryClient,
  tLDataHarmonizationV2CredentialCreateRequest: BodyType<TLDataHarmonizationV2CredentialCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateCredentialQueryKey(tLDataHarmonizationV2CredentialCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * Test connection with the provided credentials.
 * @summary Ping Credential
 */
export const pingCredential = (
  tLCredentialPingRequest: BodyType<TLCredentialPingRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/v2/data-harmonization/credentials/ping`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCredentialPingRequest,
      signal,
    },
    options,
  );
};

export const getPingCredentialQueryKey = (tLCredentialPingRequest?: BodyType<TLCredentialPingRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'credentials', 'ping', tLCredentialPingRequest] as const;
};

export const getPingCredentialQueryOptions = <
  TData = Awaited<ReturnType<typeof pingCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCredentialPingRequest: BodyType<TLCredentialPingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPingCredentialQueryKey(tLCredentialPingRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof pingCredential>>> = ({ signal }) =>
    pingCredential(tLCredentialPingRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof pingCredential>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PingCredentialQueryResult = NonNullable<Awaited<ReturnType<typeof pingCredential>>>;
export type PingCredentialQueryError = ErrorType<TLHTTPValidationError>;

export function usePingCredential<
  TData = Awaited<ReturnType<typeof pingCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCredentialPingRequest: BodyType<TLCredentialPingRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredential>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof pingCredential>>,
          TError,
          Awaited<ReturnType<typeof pingCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePingCredential<
  TData = Awaited<ReturnType<typeof pingCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCredentialPingRequest: BodyType<TLCredentialPingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredential>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof pingCredential>>,
          TError,
          Awaited<ReturnType<typeof pingCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePingCredential<
  TData = Awaited<ReturnType<typeof pingCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCredentialPingRequest: BodyType<TLCredentialPingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Ping Credential
 */

export function usePingCredential<
  TData = Awaited<ReturnType<typeof pingCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCredentialPingRequest: BodyType<TLCredentialPingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPingCredentialQueryOptions(tLCredentialPingRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Ping Credential
 */
export const prefetchPingCredentialQuery = async <
  TData = Awaited<ReturnType<typeof pingCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLCredentialPingRequest: BodyType<TLCredentialPingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPingCredentialQueryOptions(tLCredentialPingRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Ping Credential
 */
export const invalidatePingCredential = async (
  queryClient: QueryClient,
  tLCredentialPingRequest: BodyType<TLCredentialPingRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getPingCredentialQueryKey(tLCredentialPingRequest) }, options);

  return queryClient;
};

/**
 * @summary Delete Credential
 */
export const deleteCredential = (
  { credentialId }: DeleteCredentialPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/credentials/${credentialId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteCredentialQueryKey = ({ credentialId }: DeleteCredentialPathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'credentials', credentialId] as const;
};

export const getDeleteCredentialQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: DeleteCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteCredentialQueryKey({ credentialId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteCredential>>> = ({ signal }) =>
    deleteCredential({ credentialId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: credentialId !== null && credentialId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteCredential>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteCredentialQueryResult = NonNullable<Awaited<ReturnType<typeof deleteCredential>>>;
export type DeleteCredentialQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteCredential<
  TData = Awaited<ReturnType<typeof deleteCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCredentialPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCredential>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCredential>>,
          TError,
          Awaited<ReturnType<typeof deleteCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCredential<
  TData = Awaited<ReturnType<typeof deleteCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCredential>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCredential>>,
          TError,
          Awaited<ReturnType<typeof deleteCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCredential<
  TData = Awaited<ReturnType<typeof deleteCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Credential
 */

export function useDeleteCredential<
  TData = Awaited<ReturnType<typeof deleteCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: DeleteCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteCredentialQueryOptions({ credentialId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Credential
 */
export const prefetchDeleteCredentialQuery = async <
  TData = Awaited<ReturnType<typeof deleteCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { credentialId }: DeleteCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteCredentialQueryOptions({ credentialId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Credential
 */
export const invalidateDeleteCredential = async (
  queryClient: QueryClient,
  { credentialId }: DeleteCredentialPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteCredentialQueryKey({ credentialId }) }, options);

  return queryClient;
};

/**
 * @summary Get Credential By Id
 */
export const getCredentialById = (
  { credentialId }: GetCredentialByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCredentialResponse>(
    { url: `/v2/data-harmonization/credentials/${credentialId}`, method: 'GET', signal },
    options,
  );
};

export const getGetCredentialByIdQueryKey = ({ credentialId }: GetCredentialByIdPathParameters) => {
  return ['v2', 'data-harmonization', 'credentials', credentialId] as const;
};

export const getGetCredentialByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: GetCredentialByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCredentialByIdQueryKey({ credentialId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCredentialById>>> = ({ signal }) =>
    getCredentialById({ credentialId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: credentialId !== null && credentialId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getCredentialById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetCredentialByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getCredentialById>>>;
export type GetCredentialByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCredentialById<
  TData = Awaited<ReturnType<typeof getCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCredentialByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCredentialById>>,
          TError,
          Awaited<ReturnType<typeof getCredentialById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCredentialById<
  TData = Awaited<ReturnType<typeof getCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCredentialByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCredentialById>>,
          TError,
          Awaited<ReturnType<typeof getCredentialById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCredentialById<
  TData = Awaited<ReturnType<typeof getCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCredentialByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Credential By Id
 */

export function useGetCredentialById<
  TData = Awaited<ReturnType<typeof getCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: GetCredentialByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCredentialByIdQueryOptions({ credentialId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Credential By Id
 */
export const prefetchGetCredentialByIdQuery = async <
  TData = Awaited<ReturnType<typeof getCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { credentialId }: GetCredentialByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCredentialByIdQueryOptions({ credentialId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Credential By Id
 */
export const invalidateGetCredentialById = async (
  queryClient: QueryClient,
  { credentialId }: GetCredentialByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetCredentialByIdQueryKey({ credentialId }) }, options);

  return queryClient;
};

/**
 * @summary Update Credential
 */
export const updateCredential = (
  { credentialId }: UpdateCredentialPathParameters,
  tLDataHarmonizationV2CredentialUpdateRequest: BodyType<TLDataHarmonizationV2CredentialUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCredentialResponse>(
    {
      url: `/v2/data-harmonization/credentials/${credentialId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataHarmonizationV2CredentialUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateCredentialQueryKey = (
  { credentialId }: UpdateCredentialPathParameters,
  tLDataHarmonizationV2CredentialUpdateRequest?: BodyType<TLDataHarmonizationV2CredentialUpdateRequest>,
) => {
  return [
    'PATCH',
    'v2',
    'data-harmonization',
    'credentials',
    credentialId,
    tLDataHarmonizationV2CredentialUpdateRequest,
  ] as const;
};

export const getUpdateCredentialQueryOptions = <
  TData = Awaited<ReturnType<typeof updateCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: UpdateCredentialPathParameters,
  tLDataHarmonizationV2CredentialUpdateRequest: BodyType<TLDataHarmonizationV2CredentialUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUpdateCredentialQueryKey({ credentialId }, tLDataHarmonizationV2CredentialUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateCredential>>> = ({ signal }) =>
    updateCredential({ credentialId }, tLDataHarmonizationV2CredentialUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: credentialId !== null && credentialId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateCredential>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateCredentialQueryResult = NonNullable<Awaited<ReturnType<typeof updateCredential>>>;
export type UpdateCredentialQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateCredential<
  TData = Awaited<ReturnType<typeof updateCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCredentialPathParameters,
  tLDataHarmonizationV2CredentialUpdateRequest: BodyType<TLDataHarmonizationV2CredentialUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCredential>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCredential>>,
          TError,
          Awaited<ReturnType<typeof updateCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCredential<
  TData = Awaited<ReturnType<typeof updateCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCredentialPathParameters,
  tLDataHarmonizationV2CredentialUpdateRequest: BodyType<TLDataHarmonizationV2CredentialUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCredential>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCredential>>,
          TError,
          Awaited<ReturnType<typeof updateCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCredential<
  TData = Awaited<ReturnType<typeof updateCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCredentialPathParameters,
  tLDataHarmonizationV2CredentialUpdateRequest: BodyType<TLDataHarmonizationV2CredentialUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Credential
 */

export function useUpdateCredential<
  TData = Awaited<ReturnType<typeof updateCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: UpdateCredentialPathParameters,
  tLDataHarmonizationV2CredentialUpdateRequest: BodyType<TLDataHarmonizationV2CredentialUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateCredentialQueryOptions(
    { credentialId },
    tLDataHarmonizationV2CredentialUpdateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Credential
 */
export const prefetchUpdateCredentialQuery = async <
  TData = Awaited<ReturnType<typeof updateCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { credentialId }: UpdateCredentialPathParameters,
  tLDataHarmonizationV2CredentialUpdateRequest: BodyType<TLDataHarmonizationV2CredentialUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateCredentialQueryOptions(
    { credentialId },
    tLDataHarmonizationV2CredentialUpdateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Credential
 */
export const invalidateUpdateCredential = async (
  queryClient: QueryClient,
  { credentialId }: UpdateCredentialPathParameters,
  tLDataHarmonizationV2CredentialUpdateRequest: BodyType<TLDataHarmonizationV2CredentialUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateCredentialQueryKey({ credentialId }, tLDataHarmonizationV2CredentialUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Export Credential To Connector
 */
export const exportCredentialToConnector = (
  { credentialId }: ExportCredentialToConnectorPathParameters,
  tLCredentialExportToConnectorRequest: BodyType<TLCredentialExportToConnectorRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectorCredentialResponse>(
    {
      url: `/v2/data-harmonization/credentials/${credentialId}/export-to-connector`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCredentialExportToConnectorRequest,
      signal,
    },
    options,
  );
};

export const getExportCredentialToConnectorQueryKey = (
  { credentialId }: ExportCredentialToConnectorPathParameters,
  tLCredentialExportToConnectorRequest?: BodyType<TLCredentialExportToConnectorRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'credentials',
    credentialId,
    'export-to-connector',
    tLCredentialExportToConnectorRequest,
  ] as const;
};

export const getExportCredentialToConnectorQueryOptions = <
  TData = Awaited<ReturnType<typeof exportCredentialToConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: ExportCredentialToConnectorPathParameters,
  tLCredentialExportToConnectorRequest: BodyType<TLCredentialExportToConnectorRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCredentialToConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getExportCredentialToConnectorQueryKey({ credentialId }, tLCredentialExportToConnectorRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof exportCredentialToConnector>>> = ({ signal }) =>
    exportCredentialToConnector({ credentialId }, tLCredentialExportToConnectorRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: credentialId !== null && credentialId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof exportCredentialToConnector>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExportCredentialToConnectorQueryResult = NonNullable<
  Awaited<ReturnType<typeof exportCredentialToConnector>>
>;
export type ExportCredentialToConnectorQueryError = ErrorType<TLHTTPValidationError>;

export function useExportCredentialToConnector<
  TData = Awaited<ReturnType<typeof exportCredentialToConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportCredentialToConnectorPathParameters,
  tLCredentialExportToConnectorRequest: BodyType<TLCredentialExportToConnectorRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCredentialToConnector>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportCredentialToConnector>>,
          TError,
          Awaited<ReturnType<typeof exportCredentialToConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportCredentialToConnector<
  TData = Awaited<ReturnType<typeof exportCredentialToConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportCredentialToConnectorPathParameters,
  tLCredentialExportToConnectorRequest: BodyType<TLCredentialExportToConnectorRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCredentialToConnector>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportCredentialToConnector>>,
          TError,
          Awaited<ReturnType<typeof exportCredentialToConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportCredentialToConnector<
  TData = Awaited<ReturnType<typeof exportCredentialToConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportCredentialToConnectorPathParameters,
  tLCredentialExportToConnectorRequest: BodyType<TLCredentialExportToConnectorRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCredentialToConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Export Credential To Connector
 */

export function useExportCredentialToConnector<
  TData = Awaited<ReturnType<typeof exportCredentialToConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: ExportCredentialToConnectorPathParameters,
  tLCredentialExportToConnectorRequest: BodyType<TLCredentialExportToConnectorRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCredentialToConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExportCredentialToConnectorQueryOptions(
    { credentialId },
    tLCredentialExportToConnectorRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Export Credential To Connector
 */
export const prefetchExportCredentialToConnectorQuery = async <
  TData = Awaited<ReturnType<typeof exportCredentialToConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { credentialId }: ExportCredentialToConnectorPathParameters,
  tLCredentialExportToConnectorRequest: BodyType<TLCredentialExportToConnectorRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCredentialToConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExportCredentialToConnectorQueryOptions(
    { credentialId },
    tLCredentialExportToConnectorRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Export Credential To Connector
 */
export const invalidateExportCredentialToConnector = async (
  queryClient: QueryClient,
  { credentialId }: ExportCredentialToConnectorPathParameters,
  tLCredentialExportToConnectorRequest: BodyType<TLCredentialExportToConnectorRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getExportCredentialToConnectorQueryKey({ credentialId }, tLCredentialExportToConnectorRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Ping Credential By Id
 */
export const pingCredentialById = (
  { credentialId }: PingCredentialByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/credentials/${credentialId}/ping`, method: 'POST', signal },
    options,
  );
};

export const getPingCredentialByIdQueryKey = ({ credentialId }: PingCredentialByIdPathParameters) => {
  return ['POST', 'v2', 'data-harmonization', 'credentials', credentialId, 'ping'] as const;
};

export const getPingCredentialByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof pingCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: PingCredentialByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredentialById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPingCredentialByIdQueryKey({ credentialId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof pingCredentialById>>> = ({ signal }) =>
    pingCredentialById({ credentialId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: credentialId !== null && credentialId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof pingCredentialById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type PingCredentialByIdQueryResult = NonNullable<Awaited<ReturnType<typeof pingCredentialById>>>;
export type PingCredentialByIdQueryError = ErrorType<TLHTTPValidationError>;

export function usePingCredentialById<
  TData = Awaited<ReturnType<typeof pingCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PingCredentialByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredentialById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof pingCredentialById>>,
          TError,
          Awaited<ReturnType<typeof pingCredentialById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePingCredentialById<
  TData = Awaited<ReturnType<typeof pingCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PingCredentialByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredentialById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof pingCredentialById>>,
          TError,
          Awaited<ReturnType<typeof pingCredentialById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePingCredentialById<
  TData = Awaited<ReturnType<typeof pingCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PingCredentialByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredentialById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Ping Credential By Id
 */

export function usePingCredentialById<
  TData = Awaited<ReturnType<typeof pingCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: PingCredentialByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredentialById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPingCredentialByIdQueryOptions({ credentialId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Ping Credential By Id
 */
export const prefetchPingCredentialByIdQuery = async <
  TData = Awaited<ReturnType<typeof pingCredentialById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { credentialId }: PingCredentialByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof pingCredentialById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPingCredentialByIdQueryOptions({ credentialId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Ping Credential By Id
 */
export const invalidatePingCredentialById = async (
  queryClient: QueryClient,
  { credentialId }: PingCredentialByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getPingCredentialByIdQueryKey({ credentialId }) }, options);

  return queryClient;
};
